import { FC, useMemo, useState } from 'react';
import {
  FoldersNavDrawer,
  SelectItemDialog,
  TItem,
  TSelectedItem,
  TSelectItemDialogState,
  useGetVersionInfo,
  getJurisdictionId,
  TJurisdictionMap,
} from '@modules/items';
import { Path, JurisdictionFilter } from '@shared/components';
import { useFolderPathAsBreadcrumbs } from '@shared/hooks';

import TagCategoriesList from './TagCategoriesList';

import { THandleOpenAssociationDialog, TRegulatoryTagsData } from '../types';
import { FULL_PATH_TAGS_ITEM } from '../constants';
import { useAssociateTag } from '../api';
import {
  isTDocument,
  SelectHeadingDialog,
  TDraft,
  THeading,
  TSelectHeadingDialogState,
  TVersion,
  EHeadingsSelectionMode,
  EHeadingSelectionLabel,
  SELECT_HEADING_DIALOG_MODE_LOCKED_MESSAGE,
} from '@modules/documents';
import { FULL_PATH_SETUP_ITEM } from '@modules/items';

type TProps = {
  data: TRegulatoryTagsData;
};

const RegulatoryTagsContent: FC<TProps> = ({ data }) => {
  const { folder, tagsData } = data;
  const productId = folder._id;

  // Rebuild the "fullPath" for the FoldersNavDrawer
  const fullPath = [
    { _id: productId, name: folder.name, accessRight: folder.accessRight },
    FULL_PATH_SETUP_ITEM, // Setup
    FULL_PATH_TAGS_ITEM, // Tags
  ];

  // Selected jurisdictions (IDs) plus “Reusable blocks”
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>(() => {
    const defaultJurisdictionId = folder.jurisdictions?.[0]?._id;
    const blocksFolderId = folder.children.find((c) => c.isBlock)?._id;
    return [blocksFolderId, defaultJurisdictionId].filter(Boolean) as string[];
  });

  const jurisdictionsMap = useMemo(() => {
    const map: TJurisdictionMap = {};
    folder.jurisdictions.forEach((jurisdiction) => {
      map[jurisdiction._id] = jurisdiction.name;
    });
    return map;
  }, [folder.jurisdictions]);

  // --------- Association Logic ---------
  const getVersionInfo = useGetVersionInfo();
  const associateTag = useAssociateTag(productId, tagsData);

  // Dialog states
  const [selectHeadingDialogState, setSelectHeadingDialogState] = useState<TSelectHeadingDialogState | undefined>(
    undefined
  );
  const [selectItemDialogState, setSelectItemDialogState] = useState<TSelectItemDialogState | undefined>(undefined);

  // Handle association of an item to a tag
  const handleAssociate = async (tagId: string, selectedItem: TSelectedItem, headingIds?: string[]) => {
    if (!tagId) {
      console.error('No tag ID provided for association');
      return;
    }

    // Automatically add the jurisdiction ID to the list of selected folders
    const jurisId = getJurisdictionId(selectedItem as TItem);
    if (jurisId && !selectedFolderIds.includes(jurisId)) {
      setTimeout(() => {
        /**
         * If we don't use a timeout here, the auto-jurisdiction select doesn't work for tags that do not have any associations at the moment.
         * Jan Schweiger was not able to find the root cause.
         */
        setSelectedFolderIds((prev) => [...prev, jurisId]);
      }, 100);
    }

    associateTag.mutate({
      tagId,
      itemId: selectedItem._id,
      itemtype: selectedItem.type === 'DOCUMENT' ? 'DOCUMENT' : 'FILE',
      headingIds,
      dissociateItem: false,
    });
  };

  // Handle opening the item dialog
  const handleOpenItemDialog = (tagId: string) => {
    setSelectItemDialogState({
      isOpen: true,
      onSelect: async (selectedItems) => {
        await handleSelectItem(tagId, selectedItems[0]);
      },
    });
  };

  // Handle selecting an item to associate
  const handleSelectItem = async (tagId: string, selectedItem: TSelectedItem) => {
    const versionInfo = await getVersionInfo.mutateAsync({ id: selectedItem._id, itemType: selectedItem.type });
    const version = (versionInfo.draft || versionInfo.versions[0]) as TVersion | TDraft;

    const isHeadingsPresent = 'headings' in version && version.headings.length > 0;

    const initialSelectedIds = (
      tagsData
        .find((tag) => tag._id === tagId)
        ?.associatedItems.find((associatedItem) => associatedItem.item._id === selectedItem._id)?.headings || []
    ).map((heading: THeading) => heading.id);

    const mode =
      initialSelectedIds.length > 0 || isHeadingsPresent ? EHeadingsSelectionMode.SPECIFIC : EHeadingsSelectionMode.ALL;

    let lockedModeMessage;
    if (!isHeadingsPresent) lockedModeMessage = SELECT_HEADING_DIALOG_MODE_LOCKED_MESSAGE.NO_HEADINGS;

    if (isTDocument(selectedItem as TItem)) {
      setSelectHeadingDialogState({
        isOpen: true,
        documentId: selectedItem._id,
        version,
        mode,
        modeLocked: {
          isModeLocked: !isHeadingsPresent, // Lock the mode to "ALL" if there are no headings
          lockedModeMessage,
        },
        initialSelectedIds,
        onSelect: async (selectedMode, newSelectedIds) => {
          await handleAssociate(
            tagId,
            selectedItem,
            selectedMode === EHeadingsSelectionMode.ALL ? undefined : Array.from(newSelectedIds)
          );

          // Close the heading dialog
          setSelectHeadingDialogState((prev) => prev && { ...prev, isOpen: false });
        },
      });
    } else {
      await handleAssociate(tagId, selectedItem);
    }

    // Close the item dialog
    setSelectItemDialogState((prev) => prev && { ...prev, isOpen: false });
  };

  // Encapsulate the dialog handlers to pass them down the component tree
  const handleOpenAssociationDialog: THandleOpenAssociationDialog = {
    item: handleOpenItemDialog,
    heading: handleSelectItem,
  };

  return (
    <div className="flex flex-1 h-full">
      {/* Navigation drawer */}
      <FoldersNavDrawer fullPath={fullPath} selectedValue="tags" folder={folder} />

      {/* Dialogs */}
      <SelectItemDialog
        initialFolderId={productId}
        state={selectItemDialogState}
        setState={setSelectItemDialogState}
        checkManageAccess
        rootFolderId={folder._id} // limit navigation to the product folder
        title="Select item to associate"
        actionText="Associate"
        navigatableFolderTypes={['NORMAL', 'COUNTRY', 'BLOCK']}
        selectableTypes={['DOCUMENT', 'FILE', 'INLINE_BLOCK']}
      />

      <SelectHeadingDialog
        state={selectHeadingDialogState}
        setState={setSelectHeadingDialogState}
        multiSelect={true}
        label={EHeadingSelectionLabel.ASSOCIATE}
      />

      {/* Main content */}
      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className="flex flex-col space-y-3 pt-4 pb-16 pl-5 pr-8 max-w-screen-lg">
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />
            <div className="max-w-screen-sm">
              <JurisdictionFilter
                label="Filter associated files by jurisdiction:"
                folder={folder}
                withBlocks={true}
                selectedFolderIds={selectedFolderIds}
                onChangeSelectedFolderIds={setSelectedFolderIds}
              />
            </div>
            <TagCategoriesList
              handleOpenAssociationDialog={handleOpenAssociationDialog}
              jurisdictionsMap={jurisdictionsMap}
              tagsData={tagsData}
              selectedFolderIds={selectedFolderIds}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegulatoryTagsContent;
